import { useViewerHasAtLeastCustomerSupportRights } from "@/lib/rights/use-viewer-has-right";
import { isLineItemRejected } from "@/pages/(app)/returns/view/utils/get-line-items-grouped-by-categories";
import {
  ProductVariantDto,
  ReturnRequestLineItemDto,
} from "@babackapp/private-api-client/types";
import { useMemo } from "react";
import ArticleItemRejectReason from "./article-item-reject-reason";
import { ArticleItemVariant } from "./article-item-variant";

type ArticleItemSublineProps = {
  lineItem: ReturnRequestLineItemDto;
  onChangeVariant?: (variant: ProductVariantDto) => void;
  hideReturnReason?: boolean;
  hideRejectReason?: boolean;
  hideStatus?: boolean;
  hideComment?: boolean;
  hideImages?: boolean;
  hideVariant?: boolean;
};

export function ArticleItemSubline({
  lineItem,
  hideVariant,
  hideReturnReason,
  hideRejectReason,
  hideImages,
  hideComment,
  onChangeVariant,
}: ArticleItemSublineProps) {
  const hasRightToEditVariant = useViewerHasAtLeastCustomerSupportRights();

  const canEditVariant =
    hasRightToEditVariant &&
    !!onChangeVariant &&
    lineItem.resolution === "exchange";

  const items = useMemo(() => {
    return (
      [
        lineItem.sku && (
          <span key={"article-item-sku"} className="text-muted-foreground">
            {lineItem.sku}
          </span>
        ),
        !hideVariant && (
          <ArticleItemVariant
            key={"article-item-variant"}
            lineItem={lineItem}
            editable={canEditVariant}
            onChange={onChangeVariant}
          />
        ),
        lineItem.new_variant?.sku && (
          <span key={"article-item-sku"} className="text-muted-foreground">
            {lineItem.new_variant.sku}
          </span>
        ),
        !hideRejectReason && isLineItemRejected(lineItem) && (
          <span key={"article-item-reject-reason"} className="text-destructive">
            <ArticleItemRejectReason lineItem={lineItem} />
          </span>
        ),
      ].filter(Boolean) as React.ReactNode[]
    )
      .reduce<React.ReactNode[]>(
        (prev, curr, index) => [
          ...prev,
          curr,
          <span key={`separator-${index}`}>·</span>,
        ],
        [],
      )
      .slice(0, -1);
  }, [
    lineItem,
    hideVariant,
    canEditVariant,
    onChangeVariant,
    hideReturnReason,
    hideRejectReason,
    hideImages,
    hideComment,
  ]);

  return (
    <span className="text-xs flex-wrap text-muted-foreground inline-flex gap-2 items-center flex-1">
      {items}
    </span>
  );
}
